'use strict'

import {
  XRF_ALL_METALS,
  XRF_CORE_METALS,
  XRF_OPTIONAL_METALS,
} from '@/util/refinery/constants'
import {
  FEE_BASIS,
  IMPURITY_BAND_STATES,
  MASTER_LOT_STATUSES,
  OTHER_FEE_KINDS,
  TIER_DIMENSIONS,
} from '../constants'

/**
 * Internal cross-file primitives for the upstreamContract/ cluster.
 *
 * The canonical string enums live in `../constants`; this module owns the
 * cluster's pure helpers plus the draft-validation allow-lists derived from
 * those enums (ALLOWED_*). Consumers import the allow-lists via the package
 * barrel (`@/util/refinery/upstream/upstreamContract`); the unexported
 * helpers are intentionally private.
 */

// ============================================================================
// Internal helpers (not exported via barrel)
// ============================================================================

export const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
export const safeArray = (value) => (Array.isArray(value) ? value : [])
export const safeString = (value) => (typeof value === 'string' ? value : '')

export const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (isPlainObject(value)) return Object.keys(value).length > 0
  return true
}

// ============================================================================
// Enums + constants (re-exported via barrel)
// ============================================================================

const toLowerMetal = (symbol) => String(symbol).toLowerCase()

/**
 * Upstream metal vocabulary / METAL_KEYS ← XRF-all.
 * Ceiling = metals we can record via XRF. Import + freeze — do not hand-copy.
 */
export const ALLOWED_PAID_METALS = Object.freeze(XRF_ALL_METALS.map(toLowerMetal))

/** Default paid-metals authoring surface (XRF-core). */
export const DEFAULT_PAID_METALS = Object.freeze(XRF_CORE_METALS.map(toLowerMetal))

/** Optional paid-metals authoring surface (XRF-optional). */
export const OPTIONAL_PAID_METALS = Object.freeze(XRF_OPTIONAL_METALS.map(toLowerMetal))

/**
 * Default vs optional paid-metals authoring split.
 * Select pool: XRF-core always, plus any already-selected optionals (stay
 * visible when the optional gate is collapsed / unused list shrinks).
 */
export const paidMetalsSelectPool = (selected = []) => {
  const selectedSet = new Set(
    (Array.isArray(selected) ? selected : []).map((metal) => String(metal).toLowerCase()),
  )
  const optionalSelected = OPTIONAL_PAID_METALS.filter((metal) => selectedSet.has(metal))
  return [...DEFAULT_PAID_METALS, ...optionalSelected]
}

/** Optional metals not yet on paidMetals — candidates for the add-metal gate. */
export const unusedOptionalPaidMetals = (selected = []) => {
  const selectedSet = new Set(
    (Array.isArray(selected) ? selected : []).map((metal) => String(metal).toLowerCase()),
  )
  return OPTIONAL_PAID_METALS.filter((metal) => !selectedSet.has(metal))
}

// Draft-validation allow-lists, derived from their enums so accepted values
// stay in lockstep with the single source of truth.
export const ALLOWED_TIER_DIMENSIONS = Object.freeze(Object.values(TIER_DIMENSIONS))
export const ALLOWED_OTHER_FEE_CODES = ['treatment', 'copper_addition', 'sample_return', 'wire']
export const ALLOWED_OTHER_FEE_KINDS = Object.freeze(Object.values(OTHER_FEE_KINDS))
// Draft-validation allow-list, derived from the FEE_BASIS enum so the accepted
// values stay in lockstep with the single source of truth.
export const ALLOWED_FEE_BASIS = Object.freeze(Object.values(FEE_BASIS))
export const ALLOWED_CURRENCY_CODES = ['USD', 'EUR', 'GBP', 'CAD', 'JPY', 'AUD', 'CHF']

export const REFERENCE_FEE_SCHEDULE_ELEMENTS = Object.freeze([
  'sb', 'bi', 'cu', 'hg', 'te', 'cd', 'sn', 'as', 'pb', 'se', 'be',
  'ni', 'co', 'cr', 'zn', 'halides',
])
export const IMPURITY_CHARGE_BASES = Object.freeze(['perKgElement', 'perKgNetWeightReceived'])
// Draft-validation allow-list, derived from the IMPURITY_BAND_STATES enum.
export const ALLOWED_IMPURITY_BAND_STATES = Object.freeze(Object.values(IMPURITY_BAND_STATES))
export const REFINING_COST_BASES = Object.freeze(['perKgCreditedMetal'])

export const DEFAULT_EFFECTIVE_TO = '2099-12-31'

/**
 * Master lot statuses that allow re-snapshotting or switching the upstream
 * refining contract. Mirrors the server-side guard (mock API in this client
 * repo); UI and API must agree to keep the affordance and rejection error
 * aligned. Allow-list semantics: any unrecognized/future status is denied
 * by default, so adding a new status (e.g. 'paused') stays safe until
 * explicitly whitelisted here.
 */
export const CHANGE_CONTRACT_ALLOWED_STATUSES = new Set([
  MASTER_LOT_STATUSES.DRAFT,
  MASTER_LOT_STATUSES.CONFIRMED,
])
